import React from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Gift, Share, Zap } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { DesktopLayout } from "@/layouts";
import { CircularProgress } from "@/components/CircularProgress";
import { LeaderboardTab } from "@/components/LeaderboardTab";
import { useReferrals } from "@/providers/ReferralsProvider";
import { useUserEvents, useUserStats } from "@/hooks/useLeaderboard";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import type {
  LeaderboardEventWithUserStatus,
  LeaderboardUserEntry,
} from "@crypto-trading/common";
import { HowItWorks } from "@/components/HowItWorks";

/**
 * Right sidebar content for the referrals page
 */
interface ReferralCodeSidebarProps {
  referralCode: string;
  codeNeedsRefresh: boolean;
  canUseCode: boolean;
  onCopyCode: () => void;
  onShareCode: () => void;
  onGenerateNewCode: () => void;
}

const ReferralCodeSidebar: React.FC<ReferralCodeSidebarProps> = ({
  referralCode,
  codeNeedsRefresh,
  canUseCode,
  onCopyCode,
  onShareCode,
  onGenerateNewCode,
}) => {
  const handleCopyCode = async () => {
    onCopyCode();
    toast.success("Code copied to clipboard");
  };

  return (
    <div className="space-y-6 max-w-[25vw]">
      {/* Referral Code Card */}
      <div
        className="rounded-2xl p-6 relative overflow-hidden"
        style={{
          backgroundImage: "url(/cardBg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 space-y-4">
          {/* Gift Icon */}
          <div className="flex justify-center">
            <div className="w-12 h-12 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center">
              <span className="text-2xl text-accent-light">
                <Gift />
              </span>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-text-primary text-center">
            My Referral Code
          </h3>

          {canUseCode ? (
            <>
              {/* Code Display */}
              <div className="bg-primary border border-border rounded-lg overflow-hidden">
                <div className="flex items-center">
                  <input
                    type="text"
                    value={codeNeedsRefresh ? "EXPIRED" : referralCode}
                    readOnly
                    className={`bg-transparent flex-1 px-4 w-full py-3 font-mono text-lg outline-none text-center ${
                      codeNeedsRefresh ? "text-red-500" : "text-text-primary"
                    }`}
                  />
                  {!codeNeedsRefresh && (
                    <button
                      onClick={handleCopyCode}
                      className="px-4 py-3 border-l border-border text-accent hover:bg-accent/10 transition-colors flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      <span className="text-text-secondary text-sm">Copy</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Share or Generate New Code Button */}
              {codeNeedsRefresh ? (
                <button
                  onClick={onGenerateNewCode}
                  className="w-full py-3 text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-colors flex items-center justify-center gap-2 hover:bg-accent/90"
                >
                  Get New Code
                  <Zap className="w-5 h-5 text-accent-light" />
                </button>
              ) : (
                <button
                  onClick={onShareCode}
                  className="w-full py-3 text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-all duration-150 hover:bg-accent/90 flex items-center justify-center gap-2"
                >
                  Share Code
                  <Share className="w-5 h-5 text-accent-light" />
                </button>
              )}
            </>
          ) : (
            <div className="bg-primary border border-border rounded-lg p-4 space-y-3">
              <p className="text-text-secondary text-sm text-center">
                Connect your first exchange to Trigger to get your referral code
                and start earning <u className="text-accent-light">$TRIGGER</u>.
              </p>
              <Link
                to="/settings"
                className="w-full py-3 text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-all duration-150 hover:bg-accent/90 flex items-center justify-center gap-2"
              >
                Connect Exchange
                <Zap className="w-5 h-5 text-white" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="space-y-4">
        <HowItWorks />
      </div>
    </div>
  );
};

/**
 * Main content for the referrals page
 */
interface ReferralMainContentProps {
  referralCount: number;
  userPoints: number;
  userMultiplier: number;
  userRank: number | null;
  events: LeaderboardEventWithUserStatus[];
  leaderboard: LeaderboardUserEntry[];
  onShowEvents: () => void;
}

const ReferralMainContent: React.FC<ReferralMainContentProps> = ({
  referralCount,
  userPoints,
  userMultiplier,
  userRank,
  events,
  leaderboard,
  onShowEvents,
}) => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Top Prize Banner */}
      <div className="bg-tertiary border border-border rounded-2xl p-6">
        {/* Stats Section - Three Circular Progresses */}
        <div className="flex items-center justify-evenly gap-8 lg:gap-12 mb-6">
          {/* Referrals */}
          <div className="flex flex-col items-center gap-3">
            <div className="text-text-secondary text-sm font-medium">
              Referrals
            </div>
            <div className="w-24 lg:w-28">
              <CircularProgress
                progress={Math.max(
                  Math.min((referralCount / 100) * 100, 100),
                  1
                )}
                strokeWidth={6}
                value={referralCount}
              />
            </div>
          </div>

          {/* Points */}
          <div className="flex flex-col items-center gap-3">
            <div className="text-text-secondary text-sm font-medium">
              Points
            </div>
            <div className="w-24 lg:w-28">
              <CircularProgress
                progress={Math.max(
                  Math.min((userPoints / 1000) * 100, 1000),
                  1
                )}
                strokeWidth={6}
                value={userPoints}
              />
            </div>
          </div>

          {/* Multiplier */}
          <div className="flex flex-col items-center gap-3">
            <div className="text-text-secondary text-sm font-medium">
              Multiplier
            </div>
            <div className="w-24 lg:w-28">
              <CircularProgress
                progress={100}
                strokeWidth={6}
                value={`${userMultiplier}x`}
              />
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="border-t border-border my-4" />

        {/* Referral Count Text */}
        <div className="text-center text-text-primary">
          You've Referred{" "}
          <span className="text-accent font-semibold">{referralCount}</span>{" "}
          Friend{referralCount !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Level Up Your Trading Section */}
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            Level up your Trading
          </h3>
          <button
            onClick={onShowEvents}
            disabled
            className="text-accent-light text-sm font-medium hover:text-accent transition-colors"
          >
            See All
          </button>
        </div>

        {/* Event Tiles */}
        <div className="space-y-3">
          {events?.slice(0, 5).map((event) => (
            <div
              key={event.id}
              className="bg-tertiary border border-border rounded-xl p-4 flex items-center justify-between hover:border-accent/30 transition-colors"
            >
              <div className="flex-1">
                <div className="text-text-primary font-medium">
                  {event.name}
                </div>
                <div className="text-sm text-text-secondary">
                  {event.description}
                </div>
              </div>
              <div className="text-right ml-4 flex flex-col items-end gap-2 justify-between">
                {event.eventType === "LIMITED" ? (
                  <div
                    className={`text-xs ${
                      event.completed
                        ? "text-accent bg-accent/5 border border-accent text-accent-light"
                        : "text-red-200 bg-red-500/5 border border-red-500 text-red-500"
                    } rounded-full px-3 py-1`}
                  >
                    {event.completed ? "Completed" : "Not Completed"}
                  </div>
                ) : (
                  <div className="text-xs text-text-secondary">
                    {event.completedCount || 0} times
                  </div>
                )}
                <div className="text-accent-light font-semibold">
                  {event.increaseMultiplier
                    ? `${event.increaseMultiplier / 100}x multiplier`
                    : `+${event.points}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Leaderboard Section */}
      <div className="">
        <h3 className="text-lg font-semibold text-text-primary">Leaderboard</h3>
        <LeaderboardTab
          leaderboard={leaderboard}
          referralCount={referralCount}
          userRank={userRank}
          userPoints={userPoints}
        />
      </div>
    </div>
  );
};

/**
 * Desktop version of the Referrals page
 */
export const ReferralsPageDesktop: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString();
  const { data, isWhitelisted, handleCopyCode, handleShareCode } =
    useReferrals();
  const { data: events } = useUserEvents(userId);
  const { data: userStats } = useUserStats(userId);

  if (!data || !isWhitelisted) {
    return null;
  }

  const referralCode = data.referralStats.referralCode;
  const userPoints = userStats?.totalPoints ?? 0;
  const userMultiplier = userStats?.multiplier ? userStats.multiplier / 100 : 1;
  const sortedEvents =
    events?.sort(
      (a, b) =>
        new Date(b?.lastCompletedAt ?? "").getTime() -
        new Date(a?.lastCompletedAt ?? "").getTime()
    ) ?? [];

  return (
    <DesktopLayout
      pageTitle="Referrals"
      pageDescription=""
      rightSidebar={
        <ReferralCodeSidebar
          referralCode={referralCode}
          codeNeedsRefresh={data.referralStats.codeNeedsRefresh}
          canUseCode={data.referralStats.canUseCode}
          onCopyCode={handleCopyCode}
          onShareCode={handleShareCode}
          onGenerateNewCode={() => navigate("/generate-new-code")}
        />
      }
    >
      <ReferralMainContent
        referralCount={data.referralStats.totalReferrals}
        userPoints={userPoints}
        userMultiplier={userMultiplier}
        userRank={data.referralStats.userRank}
        events={sortedEvents}
        leaderboard={data.leaderboard}
        onShowEvents={() => navigate("/events")}
      />
    </DesktopLayout>
  );
};
